// /src/components/imovel/cadastro/4EtapaFinanceiroSEO.jsx
// VERSÃO 6.0 SUPREMA - AUDITORIA FINANCEIRA & ESTRUTURA SEO (TIM UNITY)

import React, { useEffect } from 'react';
import { CurrencyField, InputField, TextareaField } from './FormControls';
import { 
    FiDollarSign, FiZap, FiPercent, FiTrendingUp, 
    FiShield, FiGlobe, FiEdit3, FiActivity, FiSearch 
} from 'react-icons/fi';

export default function EtapaFinanceiroSEO({ formData, handleChange, handleValueChange }) {
    
    // 🎯 INTELIGÊNCIA TÁTICA: Valores Padrão para Fast Cadastro
    useEffect(() => {
        // Se o custo do chip estiver vazio, sugere o padrão de R$ 7,00
        if (!formData.valorCondominio) {
            handleValueChange({ target: { name: 'valorCondominio', value: '7,00' } });
        }
        // Se a fidelidade estiver vazia, sugere o padrão de 24 Meses
        if (!formData.valorIptu) {
            handleChange({ target: { name: 'valorIptu', value: '24 Meses' } });
        }
    }, []);

    // 🤖 SEO DINÂMICO (Fallbacks)
    const defaultSeoTitle = `${formData.tipoNegocio || 'Plano'} ${formData.titulo || 'TIM Empresas'}`;
    const defaultSeoDesc = `Conheça o plano ${formData.titulo || 'TIM Business'}, ideal para seu negócio. Benefícios exclusivos, voz ilimitada e apps inclusos.`;

    return (
        <div className="w-full space-y-12 animate-in fade-in zoom-in-95 duration-700 pb-20">
            
            {/* 🛡️ HEADER DE ETAPA (STEP 04) */}
            <div className="flex items-center gap-6 mb-12 border-b border-white/5 pb-8">
                <div className="relative">
                    <div className="w-16 h-16 rounded-3xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent shadow-2xl">
                        <FiDollarSign size={32} />
                    </div>
                    <div className="absolute -top-2 -right-2 w-7 h-7 bg-accent text-black rounded-full flex items-center justify-center font-black text-[12px] border-2 border-[#000022] shadow-lg">
                        04
                    </div>
                </div>
                <div>
                    <h2 className="text-3xl font-black uppercase tracking-tighter leading-none">
                        Auditoria <span className="text-accent">Financeira</span> & SEO
                    </h2>
                    <p className="text-[10px] font-black uppercase tracking-[0.4em] opacity-40 mt-2">
                        Protocolo de Tarifação e DNA de Busca Orgânica
                    </p>
                </div>
            </div>

            {/* 💰 SEÇÃO 01: INVESTIMENTO E CONTRATO */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-12 items-start w-full">
                
                {/* MENSALIDADE (Antigo Valor Venda) */}
                <div className="col-span-12 md:col-span-6">
                    <Label 
                        icon={FiTrendingUp} 
                        text="Mensalidade do Plano (R$)" 
                        accent={true} 
                        status="Obrigatório" 
                    />
                    <CurrencyField 
                        name="valorVenda" 
                        value={formData.valorVenda} 
                        onValueChange={handleValueChange} 
                        placeholder="R$ 0,00" 
                        required 
                    />
                </div>

                {/* INFO DE CONTRATO (Visual apenas) */}
                <div className="col-span-12 md:col-span-6">
                    <Label 
                        icon={FiZap} 
                        text="Identificação Sincronizada" 
                        accent={false} 
                        status="Auto" 
                    />
                    <div className="h-[64px] rounded-2xl border border-white/5 bg-white/[0.02] flex items-center px-6 font-black uppercase text-[11px] opacity-40 cursor-not-allowed">
                        {formData.titulo || 'Aguardando Etapa 01...'}
                    </div>
                </div>

                {/* CUSTO DE ATIVAÇÃO (Antigo Condomínio) */}
                <div className="col-span-12 md:col-span-4">
                    <Label icon={FiDollarSign} text="Custo do Chip / Ativação" />
                    <CurrencyField 
                        name="valorCondominio" 
                        value={formData.valorCondominio} 
                        onValueChange={handleValueChange} 
                        placeholder="R$ 7,00" 
                    />
                    <p className="text-[8px] font-bold uppercase opacity-30 mt-2 ml-2 tracking-widest">Cobrado na 1ª Fatura</p>
                </div>

                {/* PERÍODO DE FIDELIDADE (Antigo IPTU) */}
                <div className="col-span-12 md:col-span-4">
                    <Label icon={FiPercent} text="Fidelidade de Contrato" />
                    <InputField 
                        name="valorIptu" 
                        value={formData.valorIptu} 
                        onChange={handleChange} 
                        placeholder="Ex: 24 Meses" 
                    />
                </div>

                {/* STATUS DE TARIFAÇÃO */}
                <div className="col-span-12 md:col-span-4 self-end">
                    <div className="p-6 rounded-[2rem] border border-white/5 bg-accent/5 flex items-center gap-4 h-[64px]">
                        <FiActivity size={20} className="text-accent animate-pulse" />
                        <div>
                            <h5 className="text-[9px] font-black uppercase tracking-widest">Motor Comercial Ativo</h5>
                            <p className="text-[7px] font-bold opacity-40 uppercase">Valores homologados TIM UNITY</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* 🌐 SEÇÃO 02: ESTRUTURA DE SEO (INDEXAÇÃO) */}
            <div className="col-span-12 space-y-8 pt-12 border-t border-white/5 mt-10">
                <div className="flex items-center justify-between px-1">
                    <Label icon={FiGlobe} text="Configurações de SEO (Google)" accent={true} />
                    <div className="flex items-center gap-2 px-4 py-1.5 rounded-full border border-accent/20 bg-accent/5">
                         <FiSearch size={12} className="text-accent" />
                         <span className="text-[9px] font-black uppercase tracking-widest text-accent">Radar de Busca Ativo</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
                    
                    {/* META TITLE */}
                    <div className="space-y-3">
                        <div className="flex justify-between items-center px-1">
                            <span className="text-[9px] font-black uppercase opacity-30">Meta Title (Título da Guia do Navegador)</span>
                            <span className="text-[8px] font-bold opacity-20 tracking-widest">Recomendado: 60 caracteres</span>
                        </div>
                        <InputField 
                            name="seoTitulo" 
                            value={formData.seoTitulo || ''} 
                            onChange={handleChange} 
                            placeholder={defaultSeoTitle} 
                        />
                        <div className="flex items-center gap-2 px-2 opacity-40">
                            <FiEdit3 size={10} className="text-accent" />
                            <p className="text-[8px] italic uppercase font-bold tracking-tighter">Deixe vazio para usar o título comercial automático.</p>
                        </div>
                    </div>

                    {/* META DESCRIPTION */}
                    <div className="space-y-3">
                        <div className="flex justify-between items-center px-1">
                            <span className="text-[9px] font-black uppercase opacity-30">Meta Description (Resumo para o Google)</span>
                            <span className="text-[8px] font-bold opacity-20 tracking-widest">Recomendado: 160 caracteres</span>
                        </div>
                        <TextareaField 
                            name="seoDescricao" 
                            value={formData.seoDescricao || ''} 
                            onChange={handleChange} 
                            placeholder={defaultSeoDesc} 
                            rows={4}
                        />
                    </div>

                </div>
            </div>

            {/* 🛡️ FOOTER PROTOCOLAR */}
            <div className="col-span-12 p-8 rounded-[2.5rem] border border-dashed border-white/10 bg-black/20 flex items-center justify-center gap-6 opacity-40">
                <FiShield size={20} className="text-accent" />
                <span className="text-[9px] font-black uppercase tracking-[0.8em]">Protocolo de Auditoria e Indexação TIM UNITY</span>
            </div>

        </div>
    );
}

// 💎 SUB-COMPONENTE: LABEL INDUSTRIAL (V6.0)
const Label = ({ icon: Icon, text, accent, status }) => (
    <div className="flex items-center justify-between mb-4 px-1">
        <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl border flex items-center justify-center transition-all ${accent ? 'border-accent text-accent bg-accent/5' : 'border-white/10 opacity-40'}`}>
                <Icon size={18} />
            </div>
            <h3 className="text-[11px] font-black uppercase tracking-tighter opacity-70">
                {text}
            </h3>
        </div>
        {status && (
            <span className={`text-[7px] font-black uppercase tracking-widest px-2 py-1 rounded-md border ${accent ? 'text-accent border-accent/20' : 'opacity-20 border-white/10'}`}>
                {status}
            </span>
        )}
    </div>
);